import { useContext } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import ReportCard from "components/ReportCard";
import ReportShowAllCheckbox from "components/ReportShowAllCheckbox";
import { allElements } from "constants/constants";
import { OeeSummaryDashboardContext } from "contexts/OeeSummaryDashboardContext/OeeSummaryDashboardContext";
import { IProductionLineOee, toWholePercent } from "utilities/oeeSummary";
import { useStyles } from "./OeeByProductionLine.styles";
import { IOeeByProductionLineProps } from "./OeeByProductionLineInterfaces";

const barWidthPercent = (percent: number | null): number =>
    percent === null ? 0 : Math.max(0, Math.min(100, percent));

const OeeByProductionLine = ({ lines, loading }: IOeeByProductionLineProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters, showAllProductionLines, handleSelectedProductionLineChange, handleShowAllProductionLinesChange } =
        useContext(OeeSummaryDashboardContext);

    const selectLine = ({ productionLineId, productionLineName }: IProductionLineOee) =>
        handleSelectedProductionLineChange(productionLineId, productionLineName);

    const handleSelectAllChange = (checked: boolean) => {
        if (checked) {
            handleShowAllProductionLinesChange(true);
            handleSelectedProductionLineChange(allElements, "");
            return;
        }
        // Unticking scopes the report to the first line, so with no line to scope to there is nothing to
        // do and the box stays ticked - whatever the card renders around this checkbox.
        if (lines.length === 0) return;
        selectLine(lines[0]);
    };

    return (
        <ReportCard
            title={t("locations.oee_summary_dashboard.oee_by_production_line.title")}
            showNoData={!loading && lines.length === 0}
        >
            {loading ? (
                <div className={classes.loadingWrapper}>
                    <CircularProgress size={24} color="info" />
                </div>
            ) : (
                <>
                    <div className={classes.list}>
                        {lines.map((line) => {
                            const percent = toWholePercent(line.ratios.oee);
                            const barWidth = barWidthPercent(percent);
                            const selected =
                                !showAllProductionLines && filters.productionLine === line.productionLineId;
                            const dimmed = !showAllProductionLines && !selected;

                            return (
                                <button
                                    key={line.productionLineId}
                                    type="button"
                                    aria-pressed={selected}
                                    className={`${classes.row} ${dimmed ? classes.rowDimmed : classes.rowActive}`}
                                    onClick={() => selectLine(line)}
                                >
                                    <span className={classes.rowHeader}>
                                        <span
                                            data-testid="oee-line-name"
                                            title={line.productionLineName}
                                            className={`${classes.name} ${selected ? classes.nameSelected : ""}`}
                                        >
                                            {line.productionLineName}
                                        </span>
                                        <span className={classes.percent}>
                                            {percent === null
                                                ? t("locations.oee_summary_dashboard.not_applicable")
                                                : `${percent}%`}
                                        </span>
                                    </span>
                                    <span
                                        role="progressbar"
                                        aria-label={line.productionLineName}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuenow={percent === null ? undefined : barWidth}
                                        className={classes.track}
                                    >
                                        <span
                                            data-testid="oee-line-bar-fill"
                                            className={classes.fill}
                                            style={{ width: `${barWidth}%` }}
                                        />
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div className={classes.optionWrapper}>
                        <ReportShowAllCheckbox
                            checked={showAllProductionLines}
                            onChange={(_, checked) => handleSelectAllChange(checked)}
                        />
                    </div>
                </>
            )}
        </ReportCard>
    );
};

export default OeeByProductionLine;
